import { useCallback, useContext, useMemo, useState, useRef, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import PropTypes from "prop-types"
import moment from "moment"
import classNames from "classnames"
import { Badge, Divider, IconButton, Paper } from "@mui/material"
import { FilterList, ResetTvOutlined } from "@mui/icons-material"
import { isEmpty, some } from "lodash"
import { Creators as DialogCreators } from "modules/dialog/model"
import { Creators as NotificationCreators } from "modules/notifications/model"
import { getAiTermsAccepted } from "modules/account-root/selectors"
import AssetViewWrapper from "connected-components/asset-view"
import { MODEL_METADATA_SHAPE, INSTRUMENT_METADATA_SHAPE } from "connected-components/asset-view/prop-types"
import FactorsViewWrapper from "connected-components/factors-view"
import QuickAnswersWrapper from "connected-components/quick-answers"
import CompareDateSelector from "connected-components/compare/components/date-selector"
import ListItemDropDown from "components/list-item-dropdown"
import OPTooltip from "components/material/tooltip"
import { CONTENT_SET_METADATA } from "resources/content-sets"
import { useIsDashboardOptimizeExperimentsFeatureEnabled, useIsAiChatFeatureEnabled, useIsDashboardOpLayoutEnabled } from "resources/account"
import { OK, ERROR, DATA_STATUSES } from "helpers/data-statuses"
import { CUSTOM_PERIOD, TIME_FORMAT_TZ } from "helpers/dates"
import { useReduceDownloadDataFromSubscribers } from "hooks/register-downloads"
import { getDate } from "modules/intraday/selectors/dates"
import OpLayoutEngineRenderer from "connected-components/op-layout-engine/renderer"
import { TEMPLATES } from "connected-components/op-layout-engine/templates"
import { useAutoRunOptimization } from "connected-components/optimize-experiments/hooks/use-auto-run-optimization"
import KPIWrapper from "../kpi"
import GraphWrapper from "../graph"
import { DashboardContext } from "./context/dashboard"
import useTradeContext from "./context/trade"
import DashboardDownload from "./components/download"
import FilterSelector from "./components/filter-selector"
import TradeManager from "./components/trader-manager"
import { DEFAULT_MODE_SELECTOR_OPTIONS, END_VS_START_MODE_SELECTOR_OPTIONS } from "./helpers"
import { DOWNLOAD_SHEET_ORDER, getDownloadDataFileName } from "./helpers/download"
import { useAnalytics } from "connected-components/analytics"
import { DASHBOARD_EVENTS, useDashboardLoadedTracking, useModeChangeTracking, useModelChangeTracking } from "./analytics"
import { useAdjustedDashboardDates } from "./hooks/dates"
import { useIntradayWarningNotification } from "./hooks/use-intraday-warning-notification"
import { useIntradayRefresh } from "./hooks/use-intraday-refresh"
import { useDashboardOpLayoutEnginePersistence } from "./hooks/use-op-layout-engine-persistence"
import IntradayDataInfo from "./components/intraday-data-info"
import DashboardTopBar from "./components/top-bar"
import OptimizeExperimentsPanel from "connected-components/optimize-experiments/components/optimize-experiments-panel"

import "./style.scss"

const PERIOD_SELECTOR_LABELS = {
  from: "Start",
  to: "End (As of)",
}

function getModeSelectorOptions(secondInstrument, comparisonInstrument, isEndVsStartDateComparison) {
  if (!isEndVsStartDateComparison && isEmpty(secondInstrument) && isEmpty(comparisonInstrument)) {
    return null
  }

  const options = isEndVsStartDateComparison ? END_VS_START_MODE_SELECTOR_OPTIONS : DEFAULT_MODE_SELECTOR_OPTIONS

  return options.map((option) => {
    let disabled = false
    if (option.needRebalanced && isEmpty(secondInstrument)) {
      disabled = true
    }
    if (option.needComparison && isEmpty(comparisonInstrument)) {
      disabled = true
    }

    return {
      ...option,
      disabled,
    }
  })
}

export default function Dashboard(props) {
  const {
    instrument: original,
    setInstrument, filter,
    secondInstrument: rebalanced,
    comparisonInstrument: comparison,
    setComparisonInstrument,
    selectedMode,
    setSelectedMode,
    period,
    startDate,
    endDate,
    setPeriod,
    setBoundaryDates,
    useLatestDate,
    setUseLatestDate,
    setPerformanceStartDate,
    setPerformanceEndDate,
    isIntraday,
    isEndDateIntraday,
    intradayBatchMetadata,
    setIntradayBatchMetadata,
    isEndVsStartDateComparison,
    setIsEndDateIntraday,
    isPreferencesLoaded,
    setOptimizeConfiguration,
    optimizeConfiguration,
    modelId,
    base,
    isOptimizedExperimentsDrawerOpen,
  } = useContext(DashboardContext)
  const { modelMetadata, instrumentsWithMetadata, contentSets, hasMultipleInstruments, instrumentMetadata, comparisonInstrumentMetadata, baseInstrumentMetadata, lastIntradayBatch, lastIntradayBatchStatus } = props

  const analytics = useAnalytics()

  // Auto-tracking
  useDashboardLoadedTracking()
  useModeChangeTracking(selectedMode)
  useModelChangeTracking(modelId)

  const [TradeContext, tradeContextValue] = useTradeContext(DashboardContext)
  const { resetTrades, trades } = tradeContextValue

  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false)
  const [filterSelectorStatus, setFilterSelectorStatus] = useState(false)

  const isDashboardOpLayoutEnabled = useIsDashboardOpLayoutEnabled()

  const filterButtonRef = useRef(null)
  const dockviewLayoutRef = useRef(null)
  const opLayoutEnginePersistence = useDashboardOpLayoutEnginePersistence()

  const isAiTermsAccepted = useSelector(getAiTermsAccepted)
  const isAiChatFeatureEnabled = useIsAiChatFeatureEnabled()

  const dispatch = useDispatch()
  const reduceDownloadData = useReduceDownloadDataFromSubscribers(DOWNLOAD_SHEET_ORDER)
  const intradayDataAvailable = !isEmpty(lastIntradayBatch) && lastIntradayBatchStatus === OK

  useIntradayWarningNotification()

  const isOptimizeExperimentsEnabled = useIsDashboardOptimizeExperimentsFeatureEnabled()
  useAutoRunOptimization(original, modelId, endDate, base, setOptimizeConfiguration, optimizeConfiguration, setComparisonInstrument, isOptimizeExperimentsEnabled, isOptimizedExperimentsDrawerOpen)

  const currentAsOf = useMemo(() => {
    if (!intradayBatchMetadata?.asOf) {
      return "-"
    }
    return moment(intradayBatchMetadata.asOf, TIME_FORMAT_TZ).format("LTS")
  }, [intradayBatchMetadata])

  const intradayDate = getDate()

  useEffect(() => {
    setIsEndDateIntraday(endDate === intradayDate)
  }, [endDate, intradayDate, setIsEndDateIntraday])

  const modeSelectorOptions = useMemo(() =>
    getModeSelectorOptions(rebalanced, comparison, isEndVsStartDateComparison),
  [rebalanced, comparison, isEndVsStartDateComparison])

  const handleFiltersToggle = useCallback(() => {
    const newFiltersState = !filtersDrawerOpen
    setFiltersDrawerOpen(newFiltersState)

    if (newFiltersState) {
      analytics.track(DASHBOARD_EVENTS.FILTERS_OPENED)
    } else {
      analytics.track(DASHBOARD_EVENTS.FILTERS_CLOSED)
    }
  }, [filtersDrawerOpen, analytics])

  const { adjustedStartDate, earliestDate, latestDate, unmodeledDates, datesAdjusted, isLoading: isLoadingDates } = useAdjustedDashboardDates({
    modelMetadata,
    instrumentsWithMetadata,
    period,
    startDate,
    endDate,
    useLatestDate,
    setUseLatestDate,
    setPeriod,
    setBoundaryDates,
    setPerformanceStartDate,
    setPerformanceEndDate,
    trackPeriodShortcutDateChanged: (oldStartDate, newStartDate, newEndDate, period) => analytics.track(DASHBOARD_EVENTS.DATE_SHORTCUT_CHANGED, {
      oldStartDate,
      newStartDate,
      newEndDate,
      period,
    }),
    instrument: original,
    isIntraday,
    intradayDataAvailable,
  })

  const filtersCount = useMemo(() => {
    if (!filter) {
      return 0
    }
    return Object.keys(filter).length
  }, [filter])

  const isFiltersBadgeVisible = useMemo(() => {
    return !filtersDrawerOpen && filtersCount > 0
  }, [filtersDrawerOpen, filtersCount])

  useEffect(() => {
    if (instrumentMetadata?.unavailable) {
      setInstrument(null)
      dispatch(NotificationCreators.add("warning", null, "The main instrument could not be found and was reset."))
    } else if (comparisonInstrumentMetadata?.unavailable) {
      setComparisonInstrument(null)
      dispatch(NotificationCreators.add("warning", null, "The comparison instrument could not be found and was reset."))
    }
  }, [instrumentMetadata, setInstrument, setComparisonInstrument, comparisonInstrumentMetadata, dispatch])

  useEffect(() => {
    if (!isIntraday || !lastIntradayBatch || trades?.length > 0) {
      return
    }

    // Do not auto refresh if user is looking at historical data in intraday mode
    if (isIntraday && intradayDataAvailable && !isEndDateIntraday) {
      return
    }

    if (lastIntradayBatch.asOf !== intradayBatchMetadata?.asOf) {
      setIntradayBatchMetadata({
        asOf: lastIntradayBatch.asOf,
        id: lastIntradayBatch.id,
      })
    }
  }, [isIntraday, lastIntradayBatch, setIntradayBatchMetadata, intradayBatchMetadata, trades, intradayDataAvailable, isEndDateIntraday])

  useEffect(() => {
    if (isEndVsStartDateComparison && original && comparison?.id !== original.id) {
      setComparisonInstrument(original)
    }
  }, [isEndVsStartDateComparison, setComparisonInstrument, original, comparison])

  useEffect(() => {
    if (!modeSelectorOptions || !selectedMode) {
      return
    }

    const isSelectedModeAvailable = some(modeSelectorOptions, option => option.id === selectedMode && !option.disabled)
    if (!isSelectedModeAvailable) {
      const newMode = modeSelectorOptions.find(option => !option.disabled)
      if (newMode) {
        setSelectedMode(newMode.id)
      }
    }
  }, [modeSelectorOptions, selectedMode, setSelectedMode])

  const setPeriodWithChange = useCallback((change) => {
    if (typeof change === "string") {
      setPeriod(change)
    } else {
      const newStartDate = change.adjusted?.startDate || change.startDate
      const newEndDate = change.adjusted?.endDate || change.endDate
      setPeriod({
        period: CUSTOM_PERIOD,
        startDate: newStartDate,
        endDate: newEndDate,
      })

      setUseLatestDate(newEndDate === intradayDate)
      analytics.track(DASHBOARD_EVENTS.CUSTOM_DATES_CHANGED, {
        oldStartDate: startDate,
        oldEndDate: endDate,
        newStartDate: newStartDate,
        newEndDate: newEndDate,
      })
    }
  }, [setPeriod, startDate, endDate, analytics, setUseLatestDate, intradayDate])

  const handleDateChangeWithTradesCheck = useCallback((handleDateAction) => {
    if (trades?.length > 0) {
      dispatch(DialogCreators.open({
        title: "Confirm Date Change & Clear All Trades",
        contentText: "Changing the date will reset your trades. Would you still like to proceed with the date change?",
        buttons: [{
          text: "Confirm",
          type: "primary",
          action: () => {
            handleDateAction()
            resetTrades()
            analytics.track(DASHBOARD_EVENTS.TRADES_CLEARED)
          },
        }],
      }))
    } else {
      handleDateAction()
    }
  }, [trades, dispatch, resetTrades, analytics])

  const onDownloadData = useCallback(() => ({ data: reduceDownloadData(), fileName: getDownloadDataFileName(instrumentMetadata, true) }), [reduceDownloadData, instrumentMetadata])

  const { showRefreshButton, showWarning, handleIntradayRefresh, refreshButtonTooltip } = useIntradayRefresh({
    isIntraday,
    intradayDataAvailable,
    useLatestDate,
    lastIntradayBatch,
    intradayBatchMetadata,
    setIntradayBatchMetadata,
    setPeriod,
    currentPeriod: period,
    adjustedStartDate,
    trades,
    resetTrades,
    onDownloadData,
    isPreferencesLoaded,
    isEndDateIntraday,
    lastIntradayBatchStatus,
    setUseLatestDate,
  })

  const handleSetPeriod = useCallback(change => {
    handleDateChangeWithTradesCheck(
      () => setPeriodWithChange(change),
    )
  }, [handleDateChangeWithTradesCheck, setPeriodWithChange])

  const handleResetEndDate = useCallback(() => {
    const resetDate = (isIntraday && intradayDataAvailable) ? intradayDate : latestDate

    setUseLatestDate(true)
    handleDateChangeWithTradesCheck(
      () => {
        setPeriod({
          period: period,
          endDate: resetDate,
          startDate: period === CUSTOM_PERIOD ? startDate : adjustedStartDate, // Preserve custom startDate
        })
        analytics.track(DASHBOARD_EVENTS.END_DATE_RESET_TO_LATEST, { oldEndDate: endDate, newEndDate: resetDate })
      },
    )
  }, [isIntraday, intradayDataAvailable, intradayDate, latestDate, setUseLatestDate, handleDateChangeWithTradesCheck, setPeriod, period, startDate, adjustedStartDate, analytics, endDate])

  const dashboardContent = <div className="dashboard_templates-simulator">
    <div className="left-widget-panel">
      <div className="left-widget-panel-top">
        <AssetViewWrapper context={TradeContext} instrumentsWithMetadata={instrumentsWithMetadata} hasMultipleInstruments={hasMultipleInstruments} contentSets={contentSets} />
      </div>
      <div className="left-widget-panel-bottom">
        <GraphWrapper context={TradeContext} instrumentsWithMetadata={instrumentsWithMetadata} modelMetadata={modelMetadata} />
      </div>
    </div>
    <div className="right-widget-panel">
      <div className="right-widget-panel-top">
        <KPIWrapper context={TradeContext} instrumentsWithMetadata={instrumentsWithMetadata} contentSets={contentSets} />
      </div>
      <div className="right-widget-panel-bottom">
        <FactorsViewWrapper context={TradeContext} instrumentsWithMetadata={instrumentsWithMetadata} contentSets={contentSets} />
      </div>
    </div>
  </div>

  const components = useMemo(() => {
    if (!isDashboardOpLayoutEnabled) {
      return []
    }

    return [
      {
        component: "asset-view",
        title: "Asset View",
        renderer: () => (
          <div className="dv-panel-asset-view">
            <AssetViewWrapper
              context={TradeContext}
              instrumentsWithMetadata={instrumentsWithMetadata}
              hasMultipleInstruments={hasMultipleInstruments}
              contentSets={contentSets}
            />
          </div>
        ),
      },
      {
        component: "kpi",
        title: "KPI",
        renderer: () => (
          <KPIWrapper
            context={TradeContext}
            instrumentsWithMetadata={instrumentsWithMetadata}
            contentSets={contentSets}
          />
        ),
      },
      {
        component: "graph",
        title: "Graph",
        renderer: () => (
          <GraphWrapper
            context={TradeContext}
            instrumentsWithMetadata={instrumentsWithMetadata}
            modelMetadata={modelMetadata}
          />
        ),
      },
      {
        component: "factors",
        title: "Factors",
        renderer: () => (
          <div className="dv-panel-factors">
            <FactorsViewWrapper
              context={TradeContext}
              instrumentsWithMetadata={instrumentsWithMetadata}
              contentSets={contentSets}
            />
          </div>
        ),
      },
    ]
  }, [TradeContext, contentSets, hasMultipleInstruments, instrumentsWithMetadata, isDashboardOpLayoutEnabled, modelMetadata])

  const tooltipContent = <div>
    <div>Selected period: {period}</div>
    <br />
    <div><b>As of</b> date applies to point in time metrics; <b>Start</b> date used for performance, comparisons, & trends.</div>
  </div>

  return <TradeContext.Provider value={tradeContextValue}>
    <div className="modules_dashboard-root">
      <DashboardTopBar
        context={DashboardContext}
        instrumentMetadata={instrumentMetadata}
        comparisonInstrumentMetadata={comparisonInstrumentMetadata}
        baseInstrumentMetadata={baseInstrumentMetadata}
        adjustedStartDate={adjustedStartDate}
        onBaseSelected={(base, isDefault) => analytics.track(DASHBOARD_EVENTS.BASE_SELECTION, { base, isDefault })}
      />
      <Paper square className="secondary-selectors">
        <div className="left-selectors">
          <CompareDateSelector {...{
            period: period,
            earliestDate: earliestDate,
            latestDate: latestDate,
            startDate: adjustedStartDate,
            endDate: endDate,
            onChange: handleSetPeriod,
            datesAdjusted,
            unmodeledDates,
            handleResetOption: handleResetEndDate,
            isIntraday,
            intradayDataAvailable,
            isEndDateIntraday,
            intradayAsOf: currentAsOf,
            intradayBatchStatus: lastIntradayBatchStatus,
            skipAutoAdjustment: true, // Prevent double adjustment since dashboard already computed adjustedStartDate
            datesWithLabels: true,
            labels: PERIOD_SELECTOR_LABELS,
            tooltipContent,
          }}
          />
          <IntradayDataInfo
            isIntraday={isIntraday}
            isEndDateIntraday={isEndDateIntraday}
            currentAsOf={currentAsOf}
            showWarning={showWarning}
            showRefreshButton={showRefreshButton}
            onRefresh={handleIntradayRefresh}
            refreshButtonTooltip={refreshButtonTooltip}
            isLoadingDates={isLoadingDates}
            useLatestDate={useLatestDate}
          />
          {!isEmpty(modeSelectorOptions) && <>
            <Divider orientation="vertical" flexItem />
            <ListItemDropDown
              disableSubHeader
              classes={{
                toggle: "mode-selector",
              }}
              label="Showing"
              value={selectedMode}
              setValue={setSelectedMode}
              options={modeSelectorOptions}
              alignMenu="left"
              disablePortal
            />
          </>}
          <Divider orientation="vertical" flexItem />
          <TradeManager context={TradeContext} instrumentsWithMetadata={instrumentsWithMetadata} />
          {isDashboardOpLayoutEnabled && (
            <OPTooltip title="Reset Layout">
              <IconButton
                sx={{ marginLeft: -1 }}
                onClick={() => {
                  const api = dockviewLayoutRef.current?.getApi()
                  if (api) {
                    dockviewLayoutRef.current.resetLayout(api)
                  }
                }}
                size="large"
              >
                <ResetTvOutlined />
              </IconButton>
            </OPTooltip>
          )}
        </div>
        <div className="right-selectors">
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Divider orientation="vertical" flexItem />
            <DashboardDownload context={DashboardContext} id="dashboard-download-button" instrumentMetadata={instrumentMetadata} isIntraday={isIntraday} />
            <OPTooltip title={filterSelectorStatus === ERROR ? "Error loading filters. Applying filters is disabled." : "Filter positions"}>
              <span>
                <IconButton
                  onClick={handleFiltersToggle}
                  size="large"
                  color={filtersDrawerOpen ? "primary" : undefined}
                  disabled={filterSelectorStatus !== OK}
                  data-test-id="filters-toggle"
                  ref={filterButtonRef}
                >
                  <Badge overlap="rectangular" color="secondary" invisible={!isFiltersBadgeVisible} badgeContent={filtersCount}>
                    <FilterList />
                  </Badge>
                </IconButton>
              </span>
            </OPTooltip>
          </div>
        </div>
      </Paper>
      <FilterSelector context={DashboardContext} filtersDrawerOpen={filtersDrawerOpen} setFilterSelectorStatus={setFilterSelectorStatus} hasMultipleInstruments={hasMultipleInstruments} filterButtonRef={filterButtonRef.current} toggleFilters={handleFiltersToggle} />
      {isAiTermsAccepted && !isAiChatFeatureEnabled && <QuickAnswersWrapper context={TradeContext} modelMetadata={modelMetadata} instrumentsWithMetadata={instrumentsWithMetadata} />}
      <div className={classNames("dashboard-content", { "optimizer-open": isOptimizeExperimentsEnabled && isOptimizedExperimentsDrawerOpen })}>
        {isOptimizeExperimentsEnabled && (
          <OptimizeExperimentsPanel
            isOptimizedExperimentsEnabled={isOptimizeExperimentsEnabled}
            context={DashboardContext}
            modelMetadata={modelMetadata}
            instrumentsWithMetadata={instrumentsWithMetadata}
          />
        )}
        <div className="dashboard-main-content">
          {isDashboardOpLayoutEnabled ? (
            <OpLayoutEngineRenderer
              layoutRef={dockviewLayoutRef}
              components={components}
              template={TEMPLATES.HIERARCHICAL}
              persistence={opLayoutEnginePersistence}
            />
          ) : dashboardContent}
        </div>
      </div>
    </div>
  </TradeContext.Provider>
}

Dashboard.propTypes = {
  modelMetadata: MODEL_METADATA_SHAPE.isRequired,
  context: PropTypes.object.isRequired,
  instrumentMetadata: PropTypes.objectOf(INSTRUMENT_METADATA_SHAPE).isRequired,
  comparisonInstrumentMetadata: PropTypes.objectOf(INSTRUMENT_METADATA_SHAPE),
  baseInstrumentMetadata: PropTypes.objectOf(INSTRUMENT_METADATA_SHAPE),
  instrumentsWithMetadata: PropTypes.objectOf(INSTRUMENT_METADATA_SHAPE).isRequired,
  contentSets: PropTypes.arrayOf(CONTENT_SET_METADATA).isRequired,
  hasMultipleInstruments: PropTypes.bool.isRequired,
  lastIntradayBatch: PropTypes.object.isRequired,
  lastIntradayBatchStatus: PropTypes.oneOf(DATA_STATUSES).isRequired,
}
